import { z } from 'zod';
import { DocumentType, type LeadStatus, type PipelineStage } from '../enums';
import { optionalPatchText as optionalText } from './common';

/**
 * Datos del cliente editables desde la ficha: los que alimentan el perfil del comprador (distrito, ocupación,
 * nacimiento…) y, desde G2 (ux-ficha-15), también la identidad: nombres, apellidos, celular, correo y documento.
 * La API normaliza y revalida la identidad antes de guardar (409 `IDENTITY_CONFLICT` si ya es de otra persona).
 */
export const updateContactSchema = z
  .object({
    /** No puede quedar vacío: si viene, debe traer texto. */
    firstName: z.string().trim().min(1, 'El nombre no puede quedar vacío').max(120).optional(),
    lastNamePaternal: optionalText(120),
    lastNameMaternal: optionalText(120),
    phone: optionalText(40),
    email: optionalText(160),
    documentType: z.nativeEnum(DocumentType).optional().nullable(),
    documentNumber: optionalText(30),
    district: optionalText(80),
    province: optionalText(80),
    department: optionalText(80),
    occupation: optionalText(120),
    birthDate: z.coerce.date().optional().nullable(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nada que actualizar');
export type UpdateContactInput = z.infer<typeof updateContactSchema>;
export type UpdateContactPayload = z.input<typeof updateContactSchema>;

/** Claves de identidad que la ficha puede corregir; cada una se revalida contra las demás personas de la empresa. */
export const CONTACT_IDENTITY_KEYS = ['documentNumber', 'phone', 'email'] as const;
export type IdentityMatch = 'TELEFONO' | 'CORREO' | 'DOCUMENTO';
export const IDENTITY_MATCH_LABELS: Record<IdentityMatch, string> = {
  TELEFONO: 'celular',
  CORREO: 'correo',
  DOCUMENTO: 'documento',
};

export interface ContactProfileDto {
  id: string;
  firstName: string;
  lastNamePaternal: string | null;
  lastNameMaternal: string | null;
  phone: string | null;
  email: string | null;
  documentType: DocumentType | null;
  documentNumber: string | null;
  district: string | null;
  province: string | null;
  department: string | null;
  occupation: string | null;
  birthDate: string | null;
  altPhones: string[];
  altEmails: string[];
  altDocuments: string[];
}

/** Lead resumido para decidir una fusión: proyecto, etapa, asesor y si sigue activo. */
export interface ContactLeadSummary {
  id: string;
  projectId: string;
  projectName: string;
  status: LeadStatus;
  pipelineStage: PipelineStage;
  advisorName: string | null;
  capturedAt: string;
}

/** Persona tal como la ven la tarjeta de duplicados, la vista previa de la fusión y el aviso 409 de la ficha. */
export interface ContactSummary {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  documentNumber: string | null;
  altPhones: string[];
  altEmails: string[];
  altDocuments: string[];
  /** Tiene «no contactar» vigente: al fusionar, el conservado lo mantiene. */
  doNotContact: boolean;
  leads: ContactLeadSummary[];
  /** Archivos guardados «en el cliente» (se mueven al fusionar). */
  attachments: number;
  createdAt: string;
}

/**
 * Detalle del 409 al corregir la identidad desde la ficha: el dato nuevo ya pertenece a otra persona. La web ofrece
 * «Fusionar» en vez de guardar.
 */
export interface IdentityConflictDetails {
  code: 'IDENTITY_CONFLICT';
  matchedOn: IdentityMatch;
  value: string;
  contact: ContactSummary;
}

/** Distritos de Lima y Callao para sugerir al escribir (texto libre igual permitido). */
export const LIMA_DISTRICTS = [
  'Ancón',
  'Ate',
  'Barranco',
  'Breña',
  'Carabayllo',
  'Cercado de Lima',
  'Chaclacayo',
  'Chorrillos',
  'Cieneguilla',
  'Comas',
  'El Agustino',
  'Independencia',
  'Jesús María',
  'La Molina',
  'La Victoria',
  'Lince',
  'Los Olivos',
  'Lurigancho',
  'Lurín',
  'Magdalena del Mar',
  'Miraflores',
  'Pachacámac',
  'Pucusana',
  'Pueblo Libre',
  'Puente Piedra',
  'Punta Hermosa',
  'Punta Negra',
  'Rímac',
  'San Bartolo',
  'San Borja',
  'San Isidro',
  'San Juan de Lurigancho',
  'San Juan de Miraflores',
  'San Luis',
  'San Martín de Porres',
  'San Miguel',
  'Santa Anita',
  'Santa María del Mar',
  'Santa Rosa',
  'Santiago de Surco',
  'Surquillo',
  'Villa El Salvador',
  'Villa María del Triunfo',
  'Callao',
  'Bellavista',
  'Carmen de la Legua',
  'La Perla',
  'La Punta',
  'Mi Perú',
  'Ventanilla',
] as const;

/**
 * Fusión de personas repetidas: uno o varios contactos origen desaparecen dentro del conservado, en una sola
 * transacción. `sourceContactId` (uno) se mantiene por compatibilidad; `sourceContactIds` fusiona varios de una vez.
 */
export const mergeContactsSchema = z
  .object({
    /** Contacto que desaparece (sus leads, archivos y conversaciones pasan al destino). */
    sourceContactId: z.string().uuid().optional(),
    /** Varios contactos que desaparecen en una sola transacción (máximo 20). */
    sourceContactIds: z.array(z.string().uuid()).min(1).max(20).optional(),
    /** Contacto que se conserva. */
    targetContactId: z.string().uuid(),
  })
  .transform((v) => ({
    targetContactId: v.targetContactId,
    sourceContactIds: Array.from(
      new Set([...(v.sourceContactId ? [v.sourceContactId] : []), ...(v.sourceContactIds ?? [])]),
    ),
  }))
  .refine((v) => v.sourceContactIds.length > 0, 'Elige al menos un contacto para fusionar')
  .refine((v) => !v.sourceContactIds.includes(v.targetContactId), 'Elige dos contactos distintos');
export type MergeContactsInput = z.infer<typeof mergeContactsSchema>;
export type MergeContactsPayload = z.input<typeof mergeContactsSchema>;

export const mergePreviewQuerySchema = z.object({
  targetContactId: z.string().uuid(),
  /** Ids separados por coma. */
  sourceContactIds: z
    .string()
    .transform((s) =>
      Array.from(
        new Set(
          s
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean),
        ),
      ),
    )
    .pipe(z.array(z.string().uuid()).min(1).max(20)),
});

/** Dos leads activos de la misma persona en el mismo proyecto: el del absorbido se une al del conservado. */
export interface MergeLeadJoin {
  sourceLeadId: string;
  targetLeadId: string;
  projectId: string;
  projectName: string;
  sourceAdvisorName: string | null;
  targetAdvisorName: string | null;
  /** Gestiones (interacciones) y mensajes que pasan al lead conservado. */
  interactions: number;
  messages: number;
}

/** Lo que va a pasar (vista previa) o lo que pasó (resultado) al fusionar. */
export interface MergePlan {
  target: ContactSummary;
  sources: ContactSummary[];
  /** Datos del absorbido distintos a los del conservado: quedan como alternos y la identidad los encuentra. */
  alternates: { phones: string[]; emails: string[]; documents: string[] };
  /** Datos vacíos del conservado que se completan con los del absorbido. */
  filled: { phone: string | null; email: string | null; documentNumber: string | null };
  /** Leads del absorbido que pasan al conservado como leads propios (otro proyecto o ya cerrados). */
  movedLeadIds: string[];
  /** Leads activos repetidos en el mismo proyecto: se unen y el repetido se cierra con motivo «fusionado». */
  joins: MergeLeadJoin[];
  /** Archivos «del cliente» que pasan al conservado. */
  movedAttachments: number;
  /** Los documentos no coinciden entre sí: probablemente dos personas distintas. */
  differentDocuments: boolean;
}

export interface MergeContactsResult extends MergePlan {
  ok: true;
  /** Leads que llegaron al conservado como propios (sin contar los unidos). */
  movedLeads: number;
  /** Leads repetidos cerrados dentro del lead conservado. */
  joinedLeads: number;
}

export interface DuplicateGroup {
  key: string;
  matchedOn: IdentityMatch;
  contacts: ContactSummary[];
}

export interface DuplicatesResponse {
  groups: DuplicateGroup[];
  /** Hay más grupos que los mostrados (tope `limit`). */
  hasMore: boolean;
  total: number;
}
